// Libraries
import { Search2Icon } from '@chakra-ui/icons';
import { Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
// Components
import { ColumnFilterProps } from '@organisms/index';
// Types
import { DebounceFilterInputProps } from './DebounceFilterInput.types';
// Data
import { useDebouncedState } from '@hooks/index';

/**
 * A debounced filter input component for a table column.
 *
 * @template TableData - The type of data in the table.
 * @param {ColumnFilterProps<TableData>} props - The component parameters.
 * @param {Column<TableData, unknown>} props.column - The column object containing filter value and setter.
 * @param {DebounceFilterInputProps} props - The additional props for the component.
 * @param {number} [props.debounce=500] - The debounce interval in milliseconds. Default: 500.
 * @param {string} [props.placeholder] - The placeholder text for the input field.
 * @returns {ReactElement} - The rendered component.
 */
export const DebounceFilterInput = <TableData,>({
  column,
  props,
}: ColumnFilterProps<TableData, DebounceFilterInputProps>) => {
  /**
   * The debounce interval in milliseconds.
   * Default: 600
   */
  const { debounce = 600, placeholder, ...rest } = props;
  const { getFilterValue, setFilterValue } = column;
  const columnFilterValue = (getFilterValue() as string) ?? '';
  const [value, setValue] = useDebouncedState(columnFilterValue, debounce, (val) => setFilterValue(val));

  return (
    <InputGroup flexGrow={1} size='sm' mt={{ base: 0, md: 5.5 }}>
      <InputLeftElement pointerEvents='none'>
        <Search2Icon color='gray.300' />
      </InputLeftElement>
      <Input
        borderRadius='md'
        bg='white'
        h={8}
        placeholder={placeholder} // The placeholder text for the input field.
        border='greyCheck'
        _focus={{
          border: 'section',
          borderColor: 'link.base',
        }}
        _hover={{
          border: 'greyCheck',
        }}
        {...rest}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </InputGroup>
  );
};
